import React, { useEffect, useState } from 'react';
import {
    Grid,
    TextField,
    Button,
    Dialog,
    DialogContent,
    Typography,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    CircularProgress,
    FormHelperText

} from '@mui/material';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { toast } from 'react-toastify';
import AppDialogTitle from '../../../ui/appDialogTitle';
import { isLocalDev } from '../../../../utils/environment';
import { ValidDataService } from '../../../../service/validDataService';
import SupplierListDialog from '../../ValidData/validSupplier/supplierListDialog';
import SectionTitle from '../../../ui/sectionTitle';
import SimpleDatePicker from '../../../ui/simpleDatePicker';
import BrandListDialog from '../../ValidData/validSupplier/brandListDialog';
import ValidFrameBasicInfo from '../frames/validFrameBasicInfoForm';
import ValidFrameCostAndPricingForm from '../frames/validFrameCostAndPricingForm';
import { ACCEPTED_IMAGE_FILE_FORMAT, formatFileSize } from '../../../../utils';
import AttachmentUploader from '../../../common/attachmentUploader';
import lensValidationSchema, { initialValuesLensForm } from './lensValidationSchema';
import ValidLensPropertiesForm from './validLensPropertiesForm';
import labels from './labels.json';
import validLookup from '../../../../utils/validLookup';
import SafeDialogContent from 'components/ui/SafeDialogContent';

const MAX_FILE_SIZE = 10 * 1024 * 1024
const MAX_NO_OF_FILES = 10

export default function ValidLensInputForm({ open, onClose, onRecordAdded }) {
    const [supplierDialogOpen, setSupplierDialogOpen] = useState(false);
    const [brandDialogOpen, setBrandDialogOpen] = useState(false);
    const [manufacturers, setManufacturers] = useState([]);
    const [manufacturerBrands, setManufacturerBrands] = useState([]);
    const [loadingManufacturers, setLoadingManufacturers] = useState(false);
    const [loadingBrands, setLoadingBrands] = useState(false);
    const [addMfgOpen, setAddMfgOpen] = useState(false);
    const [addMfgSubmitting, setAddMfgSubmitting] = useState(false);
    const [newMfgName, setNewMfgName] = useState('');

    // Fetch manufacturers on component mount
    useEffect(() => {
        if (open) {
            fetchManufacturers();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const fetchManufacturers = async () => {
        try {
            setLoadingManufacturers(true);
            const response = await ValidDataService.getManufacturers();
            if (response?.data) {
                setManufacturers(response.data);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error('Error fetching manufacturers:', error);
            }
            toast.error('Failed to load manufacturers');
        } finally {
            setLoadingManufacturers(false);
        }
    };

    const fetchManufacturerBrands = async (manufacturerId) => {
        try {
            setLoadingBrands(true);
            const response = await ValidDataService.getManufacturerBrands(manufacturerId, 'lenses');
            if (response?.data) {
                setManufacturerBrands(response.data);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error('Error fetching brands:', error);
            }
            toast.error('Failed to load brands');
        } finally {
            setLoadingBrands(false);
        }
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {
        setSubmitting(true);
        try {
            const formattedValues = {
                ...values,
                locationId: values.location.id,
                manufacturerId: values.manufacturerId ? parseInt(values.manufacturerId, 10) : null,
                manufacturerBrandId: values.manufacturerBrandId ? parseInt(values.manufacturerBrandId, 10) : null,
                supplierCost: values.supplierCost === '' ? null : parseFloat(values.supplierCost),
                landingCost: values.landingCost === '' ? null : parseFloat(values.landingCost),
                listPrice: values.listPrice === '' ? null : parseFloat(values.listPrice),
                photos: values.photos === '' ? null : values.photos,
                type: 'lens',
                properties: {
                    ...values.properties,                 
                    lensType: values.properties.lensType === '' ? null : values.properties.lensType,
                    material: values.properties.material === '' ? null : values.properties.material,
                    lensOu: values.properties.lensOu === '' ? null : values.properties.lensOu,
                    sphere: values.properties.sphere === '' ? null : parseFloat(values.properties.sphere),
                    cyl: values.properties.cyl === '' ? null : parseFloat(values.properties.cyl),
                    axis: values.properties.axis === '' ? null : parseFloat(values.properties.axis),
                    addPower: values.properties.addPower === '' ? null : parseFloat(values.properties.addPower),
                    bc: values.properties.bc === '' ? null : parseFloat(values.properties.bc),
                    size: values.properties.size === '' ? null : parseFloat(values.properties.size),
                    ed: values.properties.ed === '' ? null : parseFloat(values.properties.ed),
                    segHt: values.properties.segHt === '' ? null : parseFloat(values.properties.segHt),
                },
                profit: values.profit === '' ? null : parseFloat(values.profit),
                profitPercentage: values.profitPercentage === '' ? null : parseFloat(values.profitPercentage),
                lastOrdered: values.lastOrdered === '' ? null : values.lastOrdered,
                lastReceived: values.lastReceived === '' ? null : values.lastReceived,
                lastSold: values.lastSold === '' ? null : values.lastSold,
                totalSold: values.totalSold === '' ? null : parseInt(values.totalSold, 10),
                returned: values.returned === '' ? null : parseInt(values.returned, 10),
                quantityOnOrder: values.quantityOnOrder === '' ? null : parseInt(values.quantityOnOrder, 10),
                quantityOnHand: values.quantityOnHand === '' ? null : parseInt(values.quantityOnHand, 10),
            };

            const response = await ValidDataService.addInventory(formattedValues)
            const { code, data, message } = response

            setSubmitting(false)

            if (code === 0) {
                toast.success(message);
                resetForm();
                onRecordAdded(data)
            } else {
                toast.error(message);
            }
        } catch (error) {
            if (isLocalDev) {
                console.error(error);
            }
            setSubmitting(false)
            toast.error('Failed to add record');
        }
    };

    const handleSupplierSelect = (supplier, setFieldValue) => {
        setFieldValue('supplierId', supplier.id);
        setFieldValue('supplierName', supplier.supplierName);
        // reset the brand
        setFieldValue('brand', '')
    };

    const handleBrandSelect = (brand, setFieldValue) => {
        //setFieldValue('supplierId', supplier.id);
        setFieldValue('brand', brand.name);
        setFieldValue('brandId', brand.id)
    };

    return (
        <Dialog open={open} maxWidth={'lg'} fullWidth>
            <AppDialogTitle title={"Add Lens"} onClose={onClose} />

            <SafeDialogContent>
                <Formik
                    initialValues={initialValuesLensForm}
                    validationSchema={lensValidationSchema}
                    onSubmit={handleSubmit}
                >
                    {({ values, setFieldValue, handleChange, handleBlur, touched, errors, isSubmitting }) => (
                        <Form>
                            <Grid container spacing={2}>
                                {/* Section 1: Manufacturer selection, after manufacturer selection than populate brands for that manufacturer */}
                                <Grid item xs={12}>
                                    <SectionTitle title="Manufacturer & Brand" />
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required error={touched.manufacturerId && Boolean(errors.manufacturerId)}>
                                        <InputLabel id="manufacturer-label">Manufacturer</InputLabel>
                                        <Select
                                            labelId="manufacturer-label"
                                            id="manufacturerId"
                                            name="manufacturerId"
                                            value={values.manufacturerId || ''}
                                            label="Manufacturer"
                                            onChange={(e) => {
                                                setFieldValue('manufacturerId', e.target.value);
                                                setFieldValue('manufacturerBrandId', ''); // Reset brand when manufacturer changes
                                                // Fetch brands for new manufacturer
                                                if (e.target.value) {
                                                    fetchManufacturerBrands(e.target.value);
                                                } else {
                                                    setManufacturerBrands([]);
                                                }
                                            }}
                                            onBlur={handleBlur}
                                            disabled={loadingManufacturers}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {manufacturers.map((manufacturer) => (
                                                <MenuItem key={manufacturer.id} value={manufacturer.id}>
                                                    {manufacturer.name}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        <Button
                                            size="small"
                                            sx={{ mt: 0.5, textTransform: 'none' }}
                                            onClick={() => setAddMfgOpen(true)}
                                        >
                                            Don’t see it? Add manufacturer
                                        </Button>
                                        {loadingManufacturers && (
                                            <CircularProgress size={20} sx={{ position: 'absolute', right: 40, top: 20 }} />
                                        )}
                                        {touched.manufacturerId && errors.manufacturerId && (
                                            <FormHelperText>{errors.manufacturerId}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth required disabled={!values.manufacturerId || loadingBrands} error={touched.manufacturerBrandId && Boolean(errors.manufacturerBrandId)}>
                                        <InputLabel id="brand-label">Brand</InputLabel>
                                        <Select
                                            labelId="brand-label"
                                            id="manufacturerBrandId"
                                            name="manufacturerBrandId"
                                            value={values.manufacturerBrandId || ''}
                                            label="Brand"
                                            onChange={(e) => setFieldValue('manufacturerBrandId', e.target.value)}
                                            onBlur={handleBlur}
                                        >
                                            <MenuItem value="">
                                                <em>None</em>
                                            </MenuItem>
                                            {manufacturerBrands.map((brand) => (
                                                <MenuItem key={brand.id} value={brand.id}>
                                                    {brand.brandName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {loadingBrands && (
                                            <CircularProgress size={20} sx={{ position: 'absolute', right: 40, top: 20 }} />
                                        )}
                                        {touched.manufacturerBrandId && errors.manufacturerBrandId && (
                                            <FormHelperText>{errors.manufacturerBrandId}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>

                                {/* <Grid container spacing={2} mt={2}> */}
                                <Grid item xs={12} sm={4}>
                                    <ValidFrameCostAndPricingForm formik={{ values, setFieldValue, handleChange, handleBlur, touched, errors }} />
                                </Grid>

                                <Grid item xs={12} sm={8}>
                                    <ValidLensPropertiesForm formik={{ values, setFieldValue, handleChange, handleBlur, touched, errors }} />
                                </Grid>

                        {/* </Grid> */}


                        {/* dont' need it for now <Grid item xs={12}>
                            <SectionTitle title="Inventory and Status" />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Ordered"}
                                onChange={(value) => setFieldValue('lastOrdered', value)}
                                value={values.lastOrdered}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Received"}
                                onChange={(value) => setFieldValue('lastReceived', value)}
                                value={values.lastReceived}
                            />
                        </Grid>
                        <Grid item xs={12} sm={3} mt={1}>
                            <SimpleDatePicker
                                label={"Last Sold"}
                                onChange={(value) => setFieldValue('lastSold', value)}
                                value={values.lastSold}
                            />
                        </Grid> 
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.totalSold}
                                onChange={handleChange}
                                name="totalSold"
                                label="Total Sold"
                                type="number"
                                size='small'
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.returned}
                                onChange={handleChange}
                                name="returned"
                                label="Returned"
                                type="number"
                                size='small'
                                fullWidth
                                disabled
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.quantityOnOrder}
                                onChange={handleChange}
                                name="quantityOnOrder"
                                label="Quantity On Order"
                                type="number"
                                size='small'
                                fullWidth
                                error={touched.quantityOnOrder && Boolean(formik.errors.quantityOnOrder)}
                                helperText={touched.quantityOnOrder && formik.errors.quantityOnOrder}
                            />
                        </Grid>
                        <Grid item xs={12} sm={2} mt={1}>
                            <TextField
                                value={values.quantityOnHand}
                                onChange={handleChange}
                                name="quantityOnHand"
                                label="Quantity On Hand"
                                type="number"
                                size='small'
                                fullWidth
                                error={touched.quantityOnHand && Boolean(formik.errors.quantityOnHand)}
                                helperText={touched.quantityOnHand && formik.errors.quantityOnHand}
                            />
                        </Grid> */}

                                <Grid item xs={12} mt={1}>
                                    <TextField
                                        value={values.notes}
                                        onChange={(e) => setFieldValue('notes', e.target.value)}
                                        name="notes"
                                        label="Notes"
                                        multiline
                                        rows={4}
                                        fullWidth
                                    />
                                </Grid>
                        {/* <Grid item xs={12}>
                            <Typography variant='caption'>Logo (JPEG/PNG files under {formatFileSize(MAX_FILE_SIZE)}). Max files allowed {MAX_NO_OF_FILES}</Typography>
                            <AttachmentUploader 
                                values={formik.values.photos}
                                onUpload={(files) => {
                                    formik.setFieldValue('photos', files);
                                    formik.setFieldTouched('photos', true);
                                }}
                                accept={
                                    ACCEPTED_IMAGE_FILE_FORMAT.reduce((acc, mimeType) => {
                                        acc[mimeType] = [];
                                        return acc;
                                    }, {})
                                }
                                maxFiles={MAX_NO_OF_FILES}
                                maxSize={MAX_FILE_SIZE}                                
                            />
                        </Grid> */}
                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={isSubmitting}
                                    >
                                        Create
                                    </Button>
                                </Grid>
                            </Grid>

                            <Dialog open={addMfgOpen} onClose={() => setAddMfgOpen(false)} maxWidth="xs" fullWidth>
                                <AppDialogTitle title="Add Manufacturer" onClose={() => setAddMfgOpen(false)} />
                                <DialogContent>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Create a manufacturer without leaving this form.
                                    </Typography>
                                    <TextField
                                        autoFocus
                                        fullWidth
                                        size="small"
                                        label="Manufacturer Name"
                                        value={newMfgName}
                                        onChange={(e) => setNewMfgName(e.target.value)}
                                        disabled={addMfgSubmitting}
                                    />
                                    <Button
                                        sx={{ mt: 2 }}
                                        variant="contained"
                                        onClick={async () => {
                                            if (!newMfgName.trim()) {
                                                toast.error('Please enter a manufacturer name');
                                                return;
                                            }

                                            setAddMfgSubmitting(true);
                                            try {
                                                const response = await ValidDataService.addValidManufacturer({
                                                    name: newMfgName.trim(),
                                                    type: 'lens',
                                                    status: validLookup.validStatus.active,
                                                });

                                                if (response.code === 0) {
                                                    toast.success('Manufacturer added');
                                                    await fetchManufacturers();
                                                    const newId = response.data?.id || '';
                                                    if (newId) {
                                                        setFieldValue('manufacturerId', newId);
                                                        setManufacturerBrands([]);
                                                    }
                                                    setNewMfgName('');
                                                    setAddMfgOpen(false);
                                                } else {
                                                    toast.error(response.message || 'Failed to add manufacturer');
                                                }
                                            } catch (error) {
                                                if (isLocalDev) {
                                                    console.error(error);
                                                }
                                                toast.error('Failed to add manufacturer');
                                            }
                                            setAddMfgSubmitting(false);
                                        }}
                                        disabled={addMfgSubmitting}
                                    >
                                        {addMfgSubmitting ? 'Saving...' : 'Save Manufacturer'}
                                    </Button>
                                </DialogContent>
                            </Dialog>
                        </Form>
                    )}
                </Formik>

                <SupplierListDialog
                    open={supplierDialogOpen}
                    onClose={() => setSupplierDialogOpen(false)}
                    onSelect={(supplier) => {
                        // We need to get setFieldValue from Formik context here
                        // This will be handled via ref or state update
                    }}
                    supplierType={validLookup.supplierType.lenses}
                />
                <BrandListDialog
                    open={brandDialogOpen}
                    onClose={() => setBrandDialogOpen(false)}
                    onSelect={(brand) => {
                        // We need to get setFieldValue from Formik context here
                        // This will be handled via ref or state update
                    }}
                    supplierId={''}
                    type='lens'
                    initialFilter={''}
                />
            </SafeDialogContent>
        </Dialog>
    );
};